import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useTheme } from '../context/ThemeContext';
import { useAlert } from '../hooks/useAlert';
import { api } from '../api';
import { NOTIFICATIONS_UPDATED_EVENT } from '../services/notificationStore';
import { routeForNotification } from '../services/webPush';
import Icon from '../components/Icon';
import { IconButton } from '../components/UI';

interface InboxNotification {
  id: string;
  type: string;
  title: string;
  body: string;
  data?: Record<string, any> | null;
  route?: string | null;
  read: boolean;
  createdAt: string;
}

function timeAgo(iso: string, t: TFunction): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return t('notifications.time.justNow');
  if (diff < 3600) return t('notifications.time.minutesAgo', { count: Math.floor(diff / 60) });
  if (diff < 86400) return t('notifications.time.hoursAgo', { count: Math.floor(diff / 3600) });
  return t('notifications.time.daysAgo', { count: Math.floor(diff / 86400) });
}

type FilterTab = 'all' | 'account' | 'transactions' | 'system';

const TAB_IDS: FilterTab[] = ['all', 'account', 'transactions', 'system'];

function categorize(n: InboxNotification): FilterTab {
  if (n.type === 'order' || /top.?up|withdraw|wallet/i.test(n.title + n.body)) return 'transactions';
  if (
    n.type === 'UPDATE' ||
    n.type === 'system' ||
    n.type === 'STREAK' ||
    n.type === 'BONUS' ||
    /^reminder_/.test(n.type) ||
    /^happy_hour/.test(n.type)
  ) return 'system';
  return 'account';
}

function NotificationItem({ item, onNavigate, onRead }: { item: InboxNotification; onNavigate: (path: string) => void; onRead: (id: string) => void }) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { alert } = useAlert();
  const isUpdate = item.type === 'UPDATE';
  const deepLink = routeForNotification({ type: item.type, route: item.route ?? undefined });

  const handleClick = () => {
    onRead(item.id);
    const apkUrl = item.data?.apkUrl as string | undefined;
    if (isUpdate && apkUrl) {
      alert(
        t('notifications.update.title'),
        t('notifications.update.confirmWeb', { version: item.title.replace(/.*v/, '') }),
        [
          { text: t('notifications.update.later'), style: 'cancel' },
          { text: t('notifications.update.downloadNow'), onPress: () => window.open(apkUrl, '_blank', 'noopener') },
        ],
      );
      return;
    }
    if (deepLink) { onNavigate(deepLink); return; }
    // Plain notification → show the full text in a popup so it's always readable.
    alert(item.title, item.body);
  };

  return (
    <div
      onClick={handleClick}
      style={{
        display: 'flex', gap: 12,
        padding: 14, borderRadius: 14,
        backgroundColor: colors.card,
        border: `1px solid ${item.read ? colors.cardBorder : colors.accent}`,
        marginBottom: 10, minHeight: 76,
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          width: 36, height: 36, borderRadius: 10,
          backgroundColor: colors.accentSoft,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon name={isUpdate ? 'download' : 'bell'} size={14} color={colors.accentText} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <div style={{
            fontSize: 13, fontWeight: 700, color: colors.text, flex: 1,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {item.title}
          </div>
          {!item.read && <div style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: colors.accent }} />}
        </div>
        <div style={{
          fontSize: 12, lineHeight: 1.5, color: colors.textSub,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>
          {item.body}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
          <div style={{ fontSize: 11, color: colors.textMuted }}>{timeAgo(item.createdAt, t)}</div>
          <div style={{ fontSize: 11, fontWeight: 600, color: colors.accentText }}>
            {deepLink ? t('notifications.tapToOpen') : t('notifications.tapToRead')}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Notifications() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [notifications, setNotifications] = useState<InboxNotification[]>([]);
  const [filter, setFilter] = useState<FilterTab>('all');

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ items: InboxNotification[]; unread: number }>('/notifications/me');
      setNotifications(res.data.items);
    } catch { /* keep last snapshot */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live-refresh when a foreground push fires the event (server already persisted).
  useEffect(() => {
    const handler = () => { load(); };
    window.addEventListener(NOTIFICATIONS_UPDATED_EVENT, handler);
    return () => window.removeEventListener(NOTIFICATIONS_UPDATED_EVENT, handler);
  }, [load]);

  const markRead = useCallback((id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    api.post(`/notifications/${id}/read`).catch(() => {});
  }, []);

  const handleMarkAllRead = async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    try { await api.post('/notifications/read-all'); } catch { /* ignore */ }
    load();
  };

  const filtered = useMemo(() => {
    if (filter === 'all') return notifications;
    return notifications.filter(n => categorize(n) === filter);
  }, [notifications, filter]);

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: colors.bg }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '14px 16px',
          paddingTop: 'calc(14px + env(safe-area-inset-top, 0px))',
          backgroundColor: colors.header,
          borderBottom: `1px solid ${colors.headerBorder}`,
          position: 'sticky', top: 0, zIndex: 10,
        }}
      >
        <IconButton icon="arrow-left" accessibilityLabel={t('actions.back')} onPress={() => navigate(-1)} />
        <div style={{ fontSize: 18, fontWeight: 800, color: colors.text }}>{t('notifications.header')}</div>
      </div>

      <div style={{ display: 'flex', gap: 8, padding: '12px 16px 8px', flexWrap: 'wrap' }}>
        {TAB_IDS.map(id => {
          const active = filter === id;
          return (
            <button
              key={id}
              onClick={() => setFilter(id)}
              style={{
                padding: '8px 14px', borderRadius: 999,
                border: `1.5px solid ${active ? colors.accent : colors.cardBorder}`,
                backgroundColor: active ? colors.accent : colors.card,
                color: active ? '#020617' : colors.textSub,
                fontSize: 12, fontWeight: 700,
                minHeight: 44, cursor: 'pointer',
              }}
            >
              {t(`notifications.tabs.${id}`)}
            </button>
          );
        })}
      </div>

      <div style={{ padding: 16 }}>
        {filtered.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 32px', textAlign: 'center' }}>
            <div style={{
              width: 110, height: 110, borderRadius: 55,
              backgroundColor: colors.accentSoft,
              border: `2px solid ${colors.accent}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 12,
            }}>
              <Icon name="envelope-open" size={44} color={colors.accentText} />
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: colors.text, marginTop: 4 }}>
              {t('notifications.empty.title')}
            </div>
            <div style={{ fontSize: 13, color: colors.textSub, lineHeight: 1.55, marginTop: 8 }}>
              {t('notifications.empty.line1')}<br />{t('notifications.empty.line2')}
            </div>
            {notifications.length > 0 && (
              <button
                onClick={handleMarkAllRead}
                style={{
                  marginTop: 18, padding: '12px 22px',
                  borderRadius: 999, border: `1.5px solid ${colors.accent}`,
                  backgroundColor: 'transparent',
                  color: colors.accentText,
                  fontSize: 13, fontWeight: 800,
                  minHeight: 50, cursor: 'pointer',
                }}
              >
                {t('notifications.empty.markAllRead')}
              </button>
            )}
          </div>
        ) : (
          filtered.map(item => <NotificationItem key={item.id} item={item} onNavigate={navigate} onRead={markRead} />)
        )}
      </div>
    </div>
  );
}
